import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import AddIcon from '@mui/icons-material/Add';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import authService from '../../../api-authorization/AuthorizeService';
import { inchesToFeet } from '../../../../utils';

const AddTractorDialog = ({ client, unaffiliatedDevices, open, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [targetApplicationRates, setTargetApplicationRates] = useState([]);
  const [externalSerialNumber, setExternalSerialNumber] = useState(null);
  const { handleSubmit, register, watch, setValue } = useForm();
  const defaultTargetApplicationRateGalAcre = watch('defaultTargetApplicationRateGalAcre', '');
  const deviceSerialNumber = watch('deviceSerialNumber', '');

  // Set default values for flow and offset fields when dialog opens
  useEffect(() => {
    if (open) {
      setValue('tankSizeGal', 400);
      setValue('targetPressurePsi', 40);
      setValue('rowMultiplier', 1);
      setValue('flowPulsesPerGal1', 333.33);
      setValue('flowPulsesPerGal2', 1);
      setValue('flowPulsesPerGal3', 1);
      setValue('xOffsetFt', 0);
      setValue('yOffsetFt', 0);
      setValue('extXOffsetFt', 0);
      setValue('extYOffsetFt', 0);
      setValue('installLocation', 'Behind Tank');
    }
  }, [open, setValue]);

  // Fetch targetApplicationRates when dialog opens or client changes
  useEffect(() => {
    const fetchRates = async () => {
      if (!client?.id) return;
      const token = await authService.getAccessToken();
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/api/clients`,
          {
            headers: !token
              ? {}
              : {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
          }
        );
        if (response.ok) {
          const resp = await response.json();
          const rates = resp.targetApplicationRates;
          const sortedRates = rates.sort((a, b) => a - b);

          setTargetApplicationRates(sortedRates);
          // Set default value if available
          if (rates.length > 0) {
            setValue('defaultTargetApplicationRateGalAcre', sortedRates[0]);
          }
        } else {
          setTargetApplicationRates([]);
        }
      } catch {
        setTargetApplicationRates([]);
      }
    };
    if (open) {
      fetchRates();
    }
  }, [client, open, setValue]);

  // Do not set default device serial number when unaffiliatedDevices changes

  const addTractor = async (data) => {
    if (
      !data.name ||
      !data.defaultTargetApplicationRateGalAcre ||
      !data.tankSizeGal ||
      !data.targetPressurePsi ||
      !data.deviceSerialNumber
    ) {
      return toast.error('Please complete all required fields.');
    }
    if (Number(data.rowMultiplier) < 1) {
      return toast.error('Row Multiplier must be 1 or greater.');
    }

    const payload = {
      clientId: client.id,
      ...data,
      externalSerialNumber,
      xOffsetFt: inchesToFeet(data.xOffsetFt),
      yOffsetFt: inchesToFeet(data.yOffsetFt),
      extXOffsetFt: inchesToFeet(data.extXOffsetFt),
      extYOffsetFt: inchesToFeet(data.extYOffsetFt)
    };
    
    const token = await authService.getAccessToken();
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/admin/tractors/add`,
        {
          method: 'POST',
          headers: !token
            ? {}
            : {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
          body: JSON.stringify(payload)
        }
      );
      if (response.ok) {
        toast.success('Sprayer added successfully!');
        onSuccess();
      } else {
        toast.error(`${response.status} ${response.statusText}`);
      }
    } catch (err) {
      toast.error('Error: ' + err);
      console.log('err', err);
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <form onSubmit={handleSubmit(addTractor)}>
        <DialogTitle>
          <AddIcon /> Add Sprayer
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Stack direction="row" spacing={2}>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>Sprayer Name</FormLabel>
                <TextField autoFocus required {...register('name')} />
              </FormControl>
            </Stack>
            <Stack direction="row" spacing={2}>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>Target App Rate (gal/acre)</FormLabel>
                <Select
                  required
                  value={defaultTargetApplicationRateGalAcre}
                  {...register('defaultTargetApplicationRateGalAcre')}
                  onChange={e => setValue('defaultTargetApplicationRateGalAcre', e.target.value)}
                  displayEmpty
                >
                  {targetApplicationRates.length === 0 && (
                    <MenuItem value="">
                      <em>No rates available</em>
                    </MenuItem>
                  )}
                  {targetApplicationRates.map((rate) => (
                    <MenuItem key={rate} value={rate}>
                      {rate}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>Target Pressure (psi)</FormLabel>
                <TextField
                  required
                  type="number"
                  {...register('targetPressurePsi')}
                  InputProps={{ inputProps: { step: 'any' } }}
                />
              </FormControl>
            </Stack>
            <Stack direction="row" spacing={2}>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>Row Multiplier</FormLabel>
                <TextField
                  required
                  type="number"
                  {...register('rowMultiplier')}
                  InputProps={{ inputProps: { step: 1, min: 1 } }}
                />
              </FormControl>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>Tank Size (gal)</FormLabel>
                <TextField
                  required
                  type="number"
                  {...register('tankSizeGal')}
                  InputProps={{ inputProps: { step: 'any' } }}
                />
              </FormControl>
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Typography variant="h6" sx={{ mb: 1 }}>
              Agtellio-S Configuration
            </Typography>

            <FormControl>
              <FormLabel>Serial Number</FormLabel>
              <Select
                value={deviceSerialNumber}
                {...register('deviceSerialNumber')}
                onChange={e => {
                  const selected = unaffiliatedDevices.find(d => (d?.serialNumber ?? d) === e.target.value);
                  setValue('deviceSerialNumber', e.target.value, { shouldDirty: true, shouldValidate: true });
                  setExternalSerialNumber(selected?.externalSerialNumber ?? null);
                }}
                displayEmpty
              >
                {(!unaffiliatedDevices || unaffiliatedDevices.length === 0) && (
                  <MenuItem value="">
                    <em>No available Agtellio-S Units</em>
                  </MenuItem>
                )}
                {unaffiliatedDevices?.map((device) => {
                  const sn = device?.serialNumber ?? device;
                  const extSn = device?.externalSerialNumber ?? null;
                  const label = extSn ? `${extSn} - ${sn}` : sn;
                  return (
                    <MenuItem key={sn} value={sn} data-external-serial-number={extSn}>
                      {label}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>Mount Location</FormLabel>
              <TextField
                required
                {...register('installLocation')}
              />
            </FormControl>

            <Divider sx={{ my: 2 }} />

            <Typography variant="h6" sx={{ mb: 1 }}>
              Flow Meter Configuration
            </Typography>

            <Stack direction="row" spacing={2}>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>Flow 1 (pul/gal)</FormLabel>
                <TextField
                  required
                  type="number"
                  {...register('flowPulsesPerGal1')}
                  InputProps={{ inputProps: { step: 'any' } }}
                />
              </FormControl>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>Flow 2 (pul/gal)</FormLabel>
                <TextField
                  required
                  type="number"
                  {...register('flowPulsesPerGal2')}
                  InputProps={{ inputProps: { step: 'any' } }}
                />
              </FormControl>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>Flow 3 (pul/gal)</FormLabel>
                <TextField
                  required
                  type="number"
                  {...register('flowPulsesPerGal3')}
                  InputProps={{ inputProps: { step: 'any' } }}
                />
              </FormControl>
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Typography variant="h6" sx={{ mb: 1 }}>
              GNSS Configuration
            </Typography>

            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              GNSS Receiver Offsets
            </Typography>
            <Stack direction="row" spacing={2}>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>+Right / -Left (in)</FormLabel>
                <TextField
                  required
                  type="number"
                  {...register('xOffsetFt')}
                  InputProps={{ inputProps: { step: 'any' } }}
                />
              </FormControl>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>+Fwd / -Back (in)</FormLabel>
                <TextField
                  required
                  type="number"
                  {...register('yOffsetFt')}
                  InputProps={{ inputProps: { step: 'any' } }}
                />
              </FormControl>
            </Stack>

            <Typography variant="subtitle2" sx={{mb: 0.5 }}>
              External GNSS Receiver Offsets
            </Typography>
            <Stack direction="row" spacing={2}>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>+Right / -Left (in)</FormLabel>
                <TextField
                  required
                  type="number"
                  {...register('extXOffsetFt')}
                  InputProps={{ inputProps: { step: 'any' } }}
                />
              </FormControl>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>+Fwd / -Back (in)</FormLabel>
                <TextField
                  required
                  type="number"
                  {...register('extYOffsetFt')}
                  InputProps={{ inputProps: { step: 'any' } }}
                />
              </FormControl>
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" variant="outlined" disabled={loading}>
            Add Sprayer
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddTractorDialog;